<template>
  <b-sidebar
    id="sidebar-create-confirmed-scientific-topic-protocol"
    sidebar-class="sidebar-lg"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
  >
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ protocol.id ? $t('Update') : $t('Create') }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="clear(hide)"
        />
      </div>

      <validation-observer ref="protocolForm">
        <b-form
          class="p-2"
          @submit.prevent
        >
          <b-form-group
            :label="$t('Protocol number')"
            label-for="protocol-number"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Protocol number')"
              rules="required"
            >
              <b-form-input
                id="protocol-number"
                v-model="protocol.protocol_number"
                trim
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('Protocol date')"
            label-for="protocol-date"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Protocol date')"
              rules="required"
            >
              <flat-pickr
                id="protocol-date"
                v-model="protocol.protocol_date"
                class="form-control"
                :config="datePickerConfig"
                placeholder="YYYY-MM-DD"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('File')"
            label-for="protocol-file"
          >
            <div
              v-if="protocol.file && !protocol.file_upload"
              class="input-group"
            >
              <div class="input-group-prepend">
                <span class="input-group-text border-success">
                  <b-link
                    :href="protocol.file"
                    download="file"
                    target="_blank"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      class="cursor-pointer"
                      size="16"
                    />
                  </b-link>
                </span>
              </div>
              <div class="form-control border-success">
                {{ $t('File') }}
              </div>
              <div class="input-group-append">
                <span class="input-group-text">
                  <feather-icon
                    icon="XIcon"
                    class="cursor-pointer danger"
                    size="16"
                    @click="cancelFile"
                  />
                </span>
              </div>
            </div>
            <validation-provider
              v-else
              #default="{ errors }"
              :name="$t('File')"
              rules="required|file-size:5"
            >
              <b-form-file
                id="protocol-file"
                v-model="protocol.file_upload"
                :placeholder="$t('Choose a file or drop it here...')"
                :drop-placeholder="$t('Drop file here...')"
                accept="application/pdf"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
              :disabled="isSubmitting"
              @click="submit(hide)"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-50"
              />
              {{ protocol.id ? $t('Update') : $t('Create') }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              @click="clear(hide)"
            >
              {{ $t('Cancel') }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BButton, BFormFile, BLink, BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver, localize } from 'vee-validate'
import flatPickr from 'vue-flatpickr-component'
import { required, fileSize } from '@/libs/validations/validations'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormFile,
    BButton,
    BLink,
    BSpinner,
    flatPickr,
    ValidationObserver,
    ValidationProvider,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      required,
      fileSize,
      isSubmitting: false,
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        allowInput: true,
        clickOpens: true,
      },
    }
  },
  computed: {
    protocol: {
      get() {
        return this.$store.state.confirmedScientificTopicProtocol.protocol
      },
      set(data) {
        this.$store.commit('confirmedScientificTopicProtocol/UPDATE', data)
      },
    },
  },
  created() {
    localize(this.$i18n.locale)
  },
  methods: {
    clear(hide) {
      this.$store.commit('confirmedScientificTopicProtocol/FRESH')
      this.$refs.protocolForm.reset()
      hide()
    },
    cancelFile() {
      this.protocol.file = null
      this.protocol.file_upload = null
    },
    submit(hide) {
      this.$refs.protocolForm.validate().then(success => {
        if (success) {
          this.isSubmitting = true
          this.$createSwal(this, this.protocol, 'confirmedScientificTopicProtocol/create', () => {
            this.$emit('refetchData')
            this.$store.commit('confirmedScientificTopicProtocol/FRESH')
            this.isSubmitting = false
            hide()
          }, () => {
            this.isSubmitting = false
          })
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.flatpickr-calendar.open {
  z-index: 1100;
}
</style>
